import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { ChevronDown, Clock, UserCheck, ShoppingBag } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import useWaitlist from "@/modules/eventRegistration/features/EventWaitlist/hooks/useWaitlist";
import { AttendanceStatus } from "@/modules/eventRegistration/features/EventWaitlist/api/fetchWaitlistDetails/types";
import RegisteredUsers from "@/modules/eventRegistration/features/EventWaitlist/components/RegisteredUsers/RegisteredUsers";
import NavigationTitleRow from "@/modules/eventRegistration/features/EventWaitlist/components/NavigationTitleRow/NavigationTitleRow";
import SessionInformationCard from "@/modules/eventRegistration/features/EventWaitlist/components/SessionInformationCard/SessionInformationCard";
import WaitlistTable from "@/modules/eventRegistration/features/EventWaitlist/components/WaitlistTable/WaitlistTable";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import ConfirmRemoveWaitlistModal from "@/modules/eventRegistration/features/EventWaitlist/components/ConfirmRemoveWaitlistModal/ConfirmRemoveWaitlistModal";
import ConfirmRegisterWaitlistModal from "@/modules/eventRegistration/features/EventWaitlist/components/ConfirmRegisterWaitlistModal/ConfirmRegisterWaitlistModal";
import ConfirmMoveEnrollmentToWaitlistModal from "@/modules/eventRegistration/features/EventWaitlist/components/ConfirmMoveEnrollmentToWaitlistModal/ConfirmMoveEnrollmentToWaitlistModal";
import ConfirmReplaceEnrollmentModal from "@/modules/eventRegistration/features/EventWaitlist/components/ConfirmReplaceEnrollmentModal/ConfirmReplaceEnrollmentModal";
import Attendances from "@/modules/eventRegistration/features/EventWaitlist/components/Attendances/Attendances";
import FinalizeSessionModal from "@/modules/eventRegistration/features/EventWaitlist/components/FinalizeSessionModal/FinalizeSessionModal";
import useEventTransactions from "@/modules/eventRegistration/features/EventWaitlist/hooks/useEventTransactions/useEventTransactions";

interface TabOption {
  value: string;
  label: string;
  badge: number;
}

export default function Waitlist() {
  const { uuid, eventUuid } = useParams<{ uuid: string; eventUuid: string }>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [isTabSheetOpen, setIsTabSheetOpen] = useState(false);

  const {
    activeTab,
    isRefreshing,
    isProcessing,
    waitlist,
    registeredUsers,
    waitlistDetails,
    loadingAttendances,
    attendanceDates,
    currentAttendances,
    selectedAttendanceDate,
    selectedAttendanceStatusId,
    setSelectedAttendanceDate,
    setSelectedAttendanceStatusId,
    showRemoveDialog,
    showMoveToWaitlistDialog,
    showReplaceDialog,
    showRegisterDialog,
    selectedUser,
    selectedRegistration,
    handleTabChange,
    refreshData,
    removeFromWaitlist,
    registerFromWaitlist,
    moveFromEnrollmentToWaitlist,
    updateWaitlistPosition,
    moveWaitlistItemToTop,
    formatDate,
    setShowRemoveDialog,
    setShowMoveToWaitlistDialog,
    setShowReplaceDialog,
    setShowRegisterDialog,
    handleRegisterClick,
    handleRemoveClick,
    handleReplaceClick,
    handleMoveToWaitlistClick,
    handleExportAttendanceCsv,
    isExportingCsv,
    updateAttendance,
    bulkUpdateAttendanceStatus,
    isUpdatingAttendance,
    showFinalizeSessionDialog,
    setShowFinalizeSessionDialog,
    finalizeSession,
  } = useWaitlist({
    eventSessionUuid: uuid,
  });

  // Get transaction count for badge (but transactions are on a separate page)
  const { registrationGroups } = useEventTransactions({ eventUuid: uuid });

  const isSessionFull =
    !!waitlistDetails && waitlistDetails.availableSeatCount <= 0;
  const availableSpots = waitlistDetails
    ? waitlistDetails.availableSeatCount
    : 0;

  // For the waitlist user
  const selectedUserFullName = selectedUser
    ? [selectedUser.firstName, selectedUser.lastName].filter(Boolean).join(" ")
    : "Unknown";

  // For the enrollment user we are demoting
  const selectedRegistrationFullName = selectedRegistration
    ? [selectedRegistration.firstName, selectedRegistration.lastName]
        .filter(Boolean)
        .join(" ")
    : "Unknown";

  // Tab options
  const tabOptions: TabOption[] = [
    { value: "waitlist", label: "Waitlist", badge: waitlist.length },
    { value: "registered", label: "Registered", badge: registeredUsers.length },
    { value: "attendance", label: "Attendance", badge: registeredUsers.length },
    {
      value: "transactions",
      label: "Transactions",
      badge: registrationGroups.length,
    },
  ];

  const activeTabOption = tabOptions.find((tab) => tab.value === activeTab);

  const handleTabSelect = (value: string) => {
    // If transactions tab is clicked, navigate to the dedicated page
    if (value === "transactions") {
      navigate(`/admin/events/${eventUuid}/sessions/${uuid}/transactions`);
      setIsTabSheetOpen(false);
      return;
    }

    handleTabChange(value);
    setIsTabSheetOpen(false);
  };

  // Get icon for each tab
  const getTabIcon = (tabValue: string) => {
    switch (tabValue) {
      case "waitlist":
        return <Clock className="h-4 w-4" />;
      case "registered":
        return <UserCheck className="h-4 w-4" />;
      case "attendance":
        return <UserCheck className="h-4 w-4" />;
      case "transactions":
        return <ShoppingBag className="h-4 w-4" />;
      default:
        return null;
    }
  };

  return (
    <MainPageWrapper>
      <div className="relative mb-8 overflow-hidden rounded-xl border border-blue-200/50 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:border-gray-700 dark:from-gray-900 dark:via-blue-900/20 dark:to-purple-900/20">
        <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
        <div className="relative p-6">
          <NavigationTitleRow
            isProcessing={isProcessing}
            isRefreshing={isRefreshing}
            refreshData={refreshData}
            waitlistLength={waitlist.length}
          />
          <SessionInformationCard
            availableSpots={availableSpots}
            waitlistDetails={waitlistDetails}
            waitlistLength={waitlist.length}
          />
        </div>
      </div>

      {isMobile && (
        <div className="mb-4">
          <Sheet onOpenChange={setIsTabSheetOpen} open={isTabSheetOpen}>
            <SheetTrigger asChild>
              <button
                className="w-full p-3 rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors"
                type="button"
              >
                <div className="flex items-center gap-2">
                  {getTabIcon(activeTab)}
                  <span className="font-medium">{activeTabOption?.label}</span>
                  <Badge variant="outline">{activeTabOption?.badge}</Badge>
                </div>
                <ChevronDown className="h-4 w-4 text-gray-500" />
              </button>
            </SheetTrigger>
            <SheetContent className="h-full w-full inset-0" side="bottom">
              <div className="flex flex-col h-full">
                <SheetHeader className="pb-6">
                  <SheetTitle className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    Select Tab
                  </SheetTitle>
                </SheetHeader>

                <div className="flex-1 pt-8">
                  <div className="space-y-3">
                    {tabOptions.map((tab) => (
                      <button
                        className={`w-full px-6 py-1.5 rounded-xl text-left flex items-center justify-between transition-colors text-sm ${
                          activeTab === tab.value
                            ? "bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-blue-900/20 dark:to-purple-900/20 text-blue-900 dark:text-blue-100 border border-blue-200/50 dark:border-gray-700/50 shadow-sm"
                            : "bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-blue-900/20 dark:to-purple-900/20 text-gray-700 dark:text-gray-300 hover:from-blue-100 hover:via-indigo-100 hover:to-purple-100 dark:hover:from-gray-800 dark:hover:via-blue-800/30 dark:hover:to-purple-800/30 border border-blue-200/30 dark:border-gray-700/30"
                        }`}
                        key={tab.value}
                        onClick={() => handleTabSelect(tab.value)}
                        type="button"
                      >
                        <div className="flex items-center gap-2">
                          {getTabIcon(tab.value)}
                          <span className="font-semibold">{tab.label}</span>
                        </div>
                        <Badge
                          className={`text-sm ${
                            activeTab === tab.value ? "border-blue-300" : ""
                          }`}
                          variant="outline"
                        >
                          {tab.badge}
                        </Badge>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      )}

      <Tabs
        className="w-full"
        onValueChange={handleTabSelect}
        value={activeTab}
      >
        {!isMobile && (
          <TabsList className="mb-4">
            {tabOptions.map((tab) => (
              <TabsTrigger
                className="flex items-center gap-2"
                key={tab.value}
                value={tab.value}
              >
                {tab.label}
                <Badge variant="outline">{tab.badge}</Badge>
              </TabsTrigger>
            ))}
          </TabsList>
        )}
        <TabsContent value="waitlist">
          <Card>
            <CardHeader>
              <CardTitle>Waitlist</CardTitle>
              <CardDescription>
                Users on the waiting list for this session.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {waitlist.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground">
                  No users on the waitlist for this session.
                </div>
              ) : (
                <WaitlistTable
                  formatDate={formatDate}
                  handleRegisterClick={handleRegisterClick}
                  handleRemoveClick={handleRemoveClick}
                  isSessionFinalized={waitlistDetails?.isSessionFinalized}
                  isSessionFull={isSessionFull}
                  onMoveToTop={moveWaitlistItemToTop}
                  onPositionChange={updateWaitlistPosition}
                  waitlist={waitlist}
                />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="registered">
          <RegisteredUsers
            formatDate={formatDate}
            handleMoveToWaitlistClick={handleMoveToWaitlistClick}
            handleReplaceClick={handleReplaceClick}
            registeredUsers={registeredUsers}
            waitlistDetails={waitlistDetails}
          />
        </TabsContent>

        <TabsContent value="attendance">
          <Card>
            <CardHeader>
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                <div>
                  <CardTitle>Attendance</CardTitle>
                  <CardDescription>
                    Track attendance for registered users in this session.
                  </CardDescription>
                </div>
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-2 justify-end">
                    <Select
                      onValueChange={(value) => {
                        setSelectedAttendanceStatusId(
                          value === "all" ? null : parseInt(value),
                        );
                      }}
                      value={selectedAttendanceStatusId?.toString() || "all"}
                    >
                      <SelectTrigger className="w-48 border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400 bg-white dark:bg-slate-800 hover:bg-blue-50 dark:hover:bg-slate-700 transition-colors font-medium">
                        <SelectValue placeholder="All Statuses" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Statuses</SelectItem>
                        {waitlistDetails?.eventSessionAttendanceStatuses?.map(
                          (status: AttendanceStatus) => (
                            <SelectItem
                              key={status.id}
                              value={status.id.toString()}
                            >
                              {status.name}
                            </SelectItem>
                          ),
                        )}
                      </SelectContent>
                    </Select>

                    <button
                      className="flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium border border-blue-600 dark:border-blue-400 text-blue-600 dark:text-blue-400 bg-white dark:bg-slate-800 hover:bg-blue-50 dark:hover:bg-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isExportingCsv}
                      onClick={handleExportAttendanceCsv}
                    >
                      {isExportingCsv ? (
                        <svg
                          className="w-4 h-4 animate-spin"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                          />
                        </svg>
                      ) : (
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                          />
                        </svg>
                      )}
                      {isExportingCsv ? (
                        "Exporting..."
                      ) : (
                        <>
                          <span className="sm:hidden">Export</span>
                          <span className="hidden sm:inline">
                            Export Attendance
                          </span>
                        </>
                      )}
                    </button>
                  </div>

                  {attendanceDates.length > 0 && (
                    <div className="flex flex-wrap gap-2 justify-end">
                      {attendanceDates.map((date) => {
                        const [year, month, day] = date.split("-").map(Number);
                        const dateObj = new Date(year, month - 1, day);
                        const formattedDate = dateObj.toLocaleDateString(
                          "en-US",
                          {
                            month: "short",
                            day: "numeric",
                          },
                        );
                        return (
                          <button
                            className={
                              selectedAttendanceDate === date
                                ? "px-4 py-1.5 rounded-md text-sm font-medium border bg-blue-600 text-white border-blue-600 transition-colors"
                                : "px-4 py-1.5 rounded-md text-sm font-medium border bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 border-blue-600 dark:border-blue-400 hover:bg-blue-50 dark:hover:bg-slate-700 transition-colors"
                            }
                            key={date}
                            onClick={() => setSelectedAttendanceDate(date)}
                          >
                            {formattedDate}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {!waitlistDetails?.isSessionFinalized &&
                    registeredUsers.length > 0 && (
                      <div className="flex justify-end">
                        <Button
                          onClick={() => setShowFinalizeSessionDialog(true)}
                          variant="default"
                        >
                          Finalize Session
                        </Button>
                      </div>
                    )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Attendances
                attendances={currentAttendances || []}
                bulkUpdateAttendanceStatus={bulkUpdateAttendanceStatus}
                isSessionFinalized={waitlistDetails?.isSessionFinalized}
                isUpdatingAttendance={isUpdatingAttendance}
                loadingAttendances={loadingAttendances}
                onRefresh={refreshData}
                updateAttendance={updateAttendance}
                waitlistDetails={waitlistDetails}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* WAITLIST MODALS */}
      {/* REMOVE FROM WAITLIST */}
      <ConfirmRemoveWaitlistModal
        isOpen={showRemoveDialog}
        onClose={() => setShowRemoveDialog(false)}
        onConfirm={async (removalReason?: string) => {
          if (selectedUser) {
            await removeFromWaitlist(selectedUser.id, removalReason);
          }
        }}
        userFullName={selectedUserFullName}
      />

      {/* REGISTER FROM WAITLIST */}
      <ConfirmRegisterWaitlistModal
        isOpen={showRegisterDialog}
        onClose={() => setShowRegisterDialog(false)}
        onConfirm={async (sendNotification: boolean) => {
          if (selectedUser) {
            await registerFromWaitlist(selectedUser.id, sendNotification);
          }
        }}
        userFullName={selectedUserFullName}
      />

      {/* MOVE ENROLLMENT -> WAITLIST */}
      <ConfirmMoveEnrollmentToWaitlistModal
        isOpen={showMoveToWaitlistDialog}
        onClose={() => setShowMoveToWaitlistDialog(false)}
        onConfirm={async (sendNotification: boolean) => {
          if (selectedRegistration) {
            await moveFromEnrollmentToWaitlist(
              selectedRegistration.id,
              sendNotification,
            );
          }
        }}
        userFullName={selectedRegistrationFullName}
      />

      {/* REPLACE ENROLLMENT */}
      <ConfirmReplaceEnrollmentModal
        eventUuid={uuid}
        isOpen={showReplaceDialog}
        onClose={() => setShowReplaceDialog(false)}
        onSuccess={refreshData}
        selectedRegistration={selectedRegistration}
      />

      {/* FINALIZE SESSION MODAL */}
      <FinalizeSessionModal
        isOpen={showFinalizeSessionDialog}
        onClose={() => setShowFinalizeSessionDialog(false)}
        onConfirm={finalizeSession}
        waitlistCount={waitlist.length}
      />
    </MainPageWrapper>
  );
}
